import React, { useEffect, useState } from "react";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
const INVOICE_STATUSES = ["draft", "sent", "paid", "overdue", "cancelled"];

export default function InvoiceFormModal({ isOpen, invoice, onClose, onSubmit }) {
  const [form, setForm] = useState({
    contactName: "",
    companyName: "",
    amountExTax: "",
    taxPercent: "18",
    status: "draft",
    issueDateUtc: "",
    dueDateUtc: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) {
      setForm({
        contactName: invoice.contactName || "",
        companyName: invoice.companyName || "",
        amountExTax: String(invoice.amountExTax ?? ""),
        taxPercent: String(invoice.taxPercent ?? 18),
        status: invoice.status || "draft",
        issueDateUtc: invoice.issueDateUtc ? new Date(invoice.issueDateUtc).toISOString().slice(0, 10) : "",
        dueDateUtc: invoice.dueDateUtc ? new Date(invoice.dueDateUtc).toISOString().slice(0, 10) : "",
        notes: invoice.notes || "",
      });
    } else {
      setForm({
        contactName: "",
        companyName: "",
        amountExTax: "",
        taxPercent: "18",
        status: "draft",
        issueDateUtc: "",
        dueDateUtc: "",
        notes: "",
      });
    }
    setErrors({});
  }, [invoice, isOpen]);

  const validate = () => {
    const e = {};
    if (!form.amountExTax || Number(form.amountExTax) < 0) e.amountExTax = "Amount is required and must be >= 0";
    if (form.taxPercent !== "" && (Number(form.taxPercent) < 0 || Number(form.taxPercent) > 100)) e.taxPercent = "Tax must be 0-100";
    if (form.issueDateUtc && form.dueDateUtc && new Date(form.dueDateUtc) < new Date(form.issueDateUtc)) e.dueDateUtc = "Due date cannot be before issue date";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSelectChange = (name, value) => {
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setSaving(true);
    try {
      await onSubmit({
        contactName: form.contactName.trim() || null,
        companyName: form.companyName.trim() || null,
        amountExTax: Number(form.amountExTax),
        taxPercent: form.taxPercent === "" ? 18 : Number(form.taxPercent),
        status: form.status,
        issueDateUtc: form.issueDateUtc ? new Date(form.issueDateUtc).toISOString() : null,
        dueDateUtc: form.dueDateUtc ? new Date(form.dueDateUtc).toISOString() : null,
        notes: form.notes.trim() || null,
      });
    } catch {
      // Parent toasts the error; modal stays open.
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">{invoice ? "Edit Invoice" : "New Invoice"}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Customer Name</label>
              <Input name="contactName" value={form.contactName} onChange={handleChange} placeholder="Customer / contact" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Company</label>
              <Input name="companyName" value={form.companyName} onChange={handleChange} placeholder="Company" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Amount (ex. tax) *</label>
              <Input name="amountExTax" type="number" value={form.amountExTax} onChange={handleChange} placeholder="50000" className={errors.amountExTax ? "border-red-500" : ""} />
              {errors.amountExTax && <p className="text-red-500 text-sm mt-1">{errors.amountExTax}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Tax %</label>
              <Input name="taxPercent" type="number" value={form.taxPercent} onChange={handleChange} placeholder="18" min="0" max="100" className={errors.taxPercent ? "border-red-500" : ""} />
              {errors.taxPercent && <p className="text-red-500 text-sm mt-1">{errors.taxPercent}</p>}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Status</label>
            <Select name="status" value={form.status} onChange={(v) => handleSelectChange("status", v)} options={INVOICE_STATUSES.map((s) => ({ value: s, label: s }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Issue Date</label>
              <Input name="issueDateUtc" type="date" value={form.issueDateUtc} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Due Date</label>
              <Input name="dueDateUtc" type="date" value={form.dueDateUtc} onChange={handleChange} className={errors.dueDateUtc ? "border-red-500" : ""} />
              {errors.dueDateUtc && <p className="text-red-500 text-sm mt-1">{errors.dueDateUtc}</p>}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <textarea name="notes" rows={2} value={form.notes} onChange={(e) => handleSelectChange("notes", e.target.value)} placeholder="Payment terms, notes..." className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2" style={{ borderColor: "#D1D5DB", minHeight: 56 }} />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" onClick={onClose} disabled={saving} className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300">Cancel</Button>
            <Button type="submit" loading={saving} className="flex-1 bg-blue-600 text-white hover:bg-blue-700">{saving ? "Saving…" : invoice ? "Update Invoice" : "Create Invoice"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

